import { t } from 'i18next';
import { urlHelper } from 'src/app/helpers';
import { getFormData } from 'src/app/helpers/form.helper';

import { apiSlice } from 'src/app/redux/api/apiSlice';
import { ImportDataType } from 'src/app/redux/interests/interest-slice.type';
import {
  SensorIdentifiedCreateDataType,
  SensorIdentifiedDataType,
  SensorIdentifiedLookupDataType,
  SensorIdentifiedLookupResultType,
  SensorIdentifiedUpdateDataType,
} from 'src/app/redux/sensors/sensor-slice.type';
import { finalUrlsConfig } from 'src/configs/url.config';

export const sensorIdentifiedSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSensorsIdentified: builder.query<SensorIdentifiedLookupResultType, SensorIdentifiedLookupDataType | void>({
      query: (params?: SensorIdentifiedLookupDataType) => ({
        url: finalUrlsConfig().interest.identified.sensor.lookup,
        params: urlHelper.getLookupQuery(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.rows.map(({ id }) => ({
                type: 'SensorIdentified' as const,
                id,
              })),
              { type: 'SensorIdentified', id: 'LIST' },
            ]
          : [{ type: 'SensorIdentified', id: 'LIST' }],
    }),
    createSensor: builder.mutation<SensorIdentifiedDataType, SensorIdentifiedCreateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.sensor.create,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'SensorIdentified', id: 'LIST' }],
    }),
    updateSensor: builder.mutation<SensorIdentifiedDataType, SensorIdentifiedUpdateDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.sensor.update,
        body: data,
        method: 'PATCH',
      }),
      invalidatesTags: (_result, _error, data) => [{ type: 'SensorIdentified', id: data.id }],
    }),
    deleteSensor: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `${finalUrlsConfig().interest.identified.sensor.delete}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'SensorIdentified', id }],
    }),
    exportSensorIdentifiedExcell: builder.mutation<ArrayBuffer, { ids?: string[] }>({
      query: (data) => ({
        url: finalUrlsConfig().exports.sensor,
        method: 'POST',
        body: data,
        responseHandler: async (response: any) => {
          if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse?.message || t('errors.defaultErrorText'));
          }
          return response.blob();
        },
        cache: 'no-cache',
      }),
    }),
    importSensor: builder.mutation<void, ImportDataType>({
      query: (data) => ({
        url: finalUrlsConfig().interest.identified.sensor.import,
        method: 'POST',
        body: getFormData(data, ['file']),
      }),

      invalidatesTags: [{ type: 'SensorIdentified', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSensorsIdentifiedQuery,
  useLazyGetSensorsIdentifiedQuery,
  useCreateSensorMutation,
  useUpdateSensorMutation,
  useDeleteSensorMutation,
  useExportSensorIdentifiedExcellMutation,
  useImportSensorMutation,
} = sensorIdentifiedSlice;
